import React from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ArrowLeft, FileText, Calendar } from 'lucide-react';
import { useNavigate, Link } from 'react-router-dom';

export function TermsOfUsePage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
      {/* Header */}
      <header className="bg-white border-b sticky top-0 z-10">
        <div className="container max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex items-center gap-2">
            <img 
              src="https://i.postimg.cc/mzV1rvRQ/logopareiad001.png" 
              alt="U-Clean" 
              className="h-8"
            />
            <span className="font-bold text-xl text-gray-800">U-Clean</span>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container max-w-4xl mx-auto px-4 py-8">
        {/* Title */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <FileText className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Termos de Uso</h1>
          <p className="text-gray-500 flex items-center justify-center gap-2">
            <Calendar className="h-4 w-4" />
            Última atualização: Março de 2026
          </p>
        </div>

        {/* Sections */}
        <Card className="mb-8">
          <CardContent className="p-6 md:p-8 prose prose-gray max-w-none">
            
            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">1. Apresentação</h2>
              <p className="text-gray-700 leading-relaxed">
                A U-Clean é uma plataforma digital de apoio à recuperação, acolhimento, desenvolvimento pessoal, 
                conexão entre usuários, acesso a conteúdos educativos, interação com profissionais e uso de 
                ferramentas de gamificação e economia interna. Ao acessar ou utilizar a plataforma, o usuário 
                declara que leu, compreendeu e concorda com estes Termos de Uso.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">2. Natureza da Plataforma</h2>
              <p className="text-gray-700 leading-relaxed">
                A U-Clean é uma rede social de apoio e desenvolvimento humano. <strong>A plataforma não substitui 
                atendimento médico, psiquiátrico, psicológico, terapêutico, hospitalar ou emergencial.</strong> Os 
                conteúdos disponíveis têm caráter informativo, educativo, comunitário e complementar.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">3. Elegibilidade</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Ao criar uma conta, o usuário declara que:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>possui capacidade legal para aceitar estes termos;</li>
                <li>fornecerá informações verdadeiras, atuais e completas;</li>
                <li>utilizará a plataforma de forma ética e responsável.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Se o usuário for menor de idade, o uso deve ocorrer conforme a legislação aplicável e, quando 
                necessário, com supervisão do responsável legal.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">4. Cadastro e Conta</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                O usuário é responsável pela segurança de sua conta, senha e dados de acesso. É proibido:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>compartilhar credenciais de acesso com terceiros;</li>
                <li>criar contas falsas ou enganosas;</li>
                <li>utilizar identidade de outra pessoa;</li>
                <li>tentar acessar áreas restritas sem autorização.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                A U-Clean poderá suspender ou encerrar contas em caso de fraude, abuso, risco à segurança, 
                violação destes termos ou comportamento prejudicial à comunidade.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">5. Regras da Comunidade</h2>
              <p className="text-gray-700 leading-relaxed mb-4">Não é permitido publicar, transmitir ou incentivar:</p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>discurso de ódio, assédio, discriminação ou ameaças;</li>
                <li>conteúdo ilegal ou violento;</li>
                <li>incentivo ao uso de drogas, álcool, jogos ou comportamentos autodestrutivos;</li>
                <li>exploração da vulnerabilidade emocional de outros usuários;</li>
                <li>spam, propaganda enganosa ou captação indevida;</li>
                <li>exposição de dados pessoais de terceiros sem autorização;</li>
                <li>informações falsas sobre tratamentos, curas ou promessas enganosas.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                A U-Clean poderá moderar, editar, ocultar ou excluir conteúdos que violem estas regras, 
                bem como advertir, limitar ou bloquear usuários.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">6. Conteúdo do Usuário</h2>
              <p className="text-gray-700 leading-relaxed">
                O usuário permanece titular do conteúdo que publica, mas concede à U-Clean licença não exclusiva 
                para armazenar, exibir, processar e disponibilizar esse conteúdo dentro da plataforma, 
                exclusivamente para funcionamento, segurança, melhoria do serviço e divulgação institucional 
                da própria plataforma, sempre respeitando a legislação aplicável.
              </p>
              <p className="text-gray-700 leading-relaxed mt-4">
                <strong>O usuário é integralmente responsável pelos conteúdos que publica.</strong>
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">7. Profissionais, Cursos e Conteúdos</h2>
              <p className="text-gray-700 leading-relaxed">
                A presença de profissionais, cursos, programas, trilhas, grupos ou conteúdos dentro da 
                plataforma não representa garantia de resultado individual. A U-Clean poderá estabelecer 
                critérios de verificação, qualificação, exibição e remoção de profissionais e conteúdos, 
                sempre buscando segurança, ética e qualidade.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">8. Pagamentos, Planos e Acessos</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                A plataforma poderá oferecer planos gratuitos e pagos, cursos, produtos, assinaturas, 
                benefícios e recursos premium. Os detalhes de preço, duração, acesso, renovação, 
                cancelamento e benefícios poderão ser apresentados nas respectivas páginas comerciais.
              </p>
              <p className="text-gray-700 leading-relaxed mb-4">
                A U-Clean poderá suspender, revogar ou ajustar acessos em caso de:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>fraude;</li>
                <li>chargeback;</li>
                <li>reembolso;</li>
                <li>uso indevido de benefício;</li>
                <li>liberação promocional expirada;</li>
                <li>erro operacional ou técnico.</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">9. Tokens, Colecionáveis Digitais e Economia Interna</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                A plataforma poderá disponibilizar tokens, créditos, pontos, itens digitais, colecionáveis 
                e outros recursos internos para gamificação, reconhecimento, benefícios ou aquisição de 
                funcionalidades. Tais itens:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>podem ter utilidade dentro do ecossistema;</li>
                <li>podem possuir regras próprias de uso, validade, raridade, limitação ou transferência;</li>
                <li><strong>não constituem promessa de investimento, valorização financeira ou retorno garantido.</strong></li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                Caso a U-Clean adote no futuro tecnologias de blockchain, NFTs ou ativos digitais 
                certificados, regras adicionais poderão ser publicadas em documento específico.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">10. Limitação de Responsabilidade</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                A U-Clean não garante que a plataforma será ininterrupta, livre de falhas ou adequada 
                para todos os objetivos individuais. A plataforma não se responsabiliza por:
              </p>
              <ul className="list-disc pl-6 text-gray-700 space-y-2">
                <li>decisões tomadas por usuários com base em conteúdos da comunidade;</li>
                <li>condutas de terceiros;</li>
                <li>danos decorrentes de uso indevido da conta;</li>
                <li>falhas temporárias, indisponibilidades ou integrações externas.</li>
              </ul>
              <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-800 font-medium">
                  ⚠️ Em situações de crise, risco à vida, urgência médica ou emergência psiquiátrica, 
                  o usuário deve procurar imediatamente os serviços competentes de saúde e emergência.
                </p>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">11. Privacidade e Proteção de Dados</h2>
              <p className="text-gray-700 leading-relaxed">
                O tratamento de dados pessoais segue a{' '}
                <Link to="/privacidade" className="text-blue-600 hover:underline">
                  Política de Privacidade
                </Link>{' '}
                da U-Clean e a legislação aplicável, inclusive a LGPD.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">12. Suspensão e Encerramento</h2>
              <p className="text-gray-700 leading-relaxed">
                A U-Clean poderá, a qualquer momento, limitar, suspender ou encerrar contas, conteúdos, 
                acessos e funcionalidades para proteger a comunidade, cumprir obrigações legais, 
                preservar a segurança da plataforma ou fazer ajustes operacionais.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-4">13. Alterações destes Termos</h2>
              <p className="text-gray-700 leading-relaxed">
                A U-Clean poderá atualizar estes Termos de Uso periodicamente. O uso continuado da 
                plataforma após a atualização será interpretado como concordância com a nova versão.
              </p>
            </section>

            <section className="mb-4">
              <h2 className="text-xl font-bold text-gray-900 mb-4">14. Contato</h2>
              <p className="text-gray-700 leading-relaxed">
                Dúvidas relacionadas a estes Termos podem ser encaminhadas pelos canais oficiais da plataforma.
              </p>
            </section>

          </CardContent>
        </Card>

        {/* Related Links */}
        <div className="flex flex-wrap gap-4 justify-center mb-8">
          <Link to="/privacidade">
            <Button variant="outline">Política de Privacidade</Button>
          </Link>
          <Link to="/aviso-importante">
            <Button variant="outline">Aviso Importante</Button>
          </Link>
        </div>

        {/* Back Button */}
        <div className="text-center">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            Voltar
          </Button>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-gray-100 border-t py-6 mt-8">
        <div className="container max-w-4xl mx-auto px-4 text-center text-sm text-gray-500">
          © {new Date().getFullYear()} U-Clean. Todos os direitos reservados.
        </div>
      </footer>
    </div>
  );
}

export default TermsOfUsePage;
